/**
 * EdgeOrchestrator
 *
 * E018-S009: Orchestrates the reactive edge pipeline.
 * Wires together all services and manages the reactive data flow.
 */

import { I18n } from '@affine/i18n';
import {
  type AutomationBlockModel,
  writeCleanupBinding,
} from '@blocksuite/affine/blocks/streamlain';
import type { Bound } from '@blocksuite/affine/global/gfx';
import { NotificationProvider } from '@blocksuite/affine-shared/services';
import {
  cancelGfxViewportFrame,
  type GfxController,
  type GfxModel,
  measureGfxPerformance,
  scheduleGfxViewportFrame,
} from '@blocksuite/std/gfx';
import type { Store } from '@blocksuite/store';
import {
  combineLatest,
  from,
  map,
  merge,
  type Observable,
  Subject,
  type Subscription,
  switchMap,
  tap,
} from 'rxjs';

import type { IBlockRegistry } from '../core/interfaces/IBlockRegistry';
import type { IEdgeOrchestrator } from '../core/interfaces/IEdgeOrchestrator';
import type { IEdgeRenderingService } from '../core/interfaces/IEdgeRenderingService';
import type {
  IncomingAutomationInfo,
  TrashedLocalAutomationInfo,
  WorkspaceDatabaseInfo,
} from '../core/interfaces/IGhostDerivationService';
import type {
  GhostMutationScope,
  GhostSyncIntent,
} from '../core/interfaces/IGhostSyncService';
import type { AutomationEdgeMetadata } from '../core/types/edge-definition-types';
import { EdgeDerivationService } from '../derivation/EdgeDerivationService';
import { GhostDerivationService } from '../derivation/GhostDerivationService';
import { BlockRegistry } from '../registry/BlockRegistry';
import { CanvasReadyGate } from '../registry/CanvasReadyGate';
import { EdgeHighlightService } from '../services/edge-highlight-service';
import { EdgeSelectionService } from '../services/edge-selection-service';
import { EdgeSyncService } from '../sync/EdgeSyncService';
import { GhostSyncService } from '../sync/GhostSyncService';
import type { StreamlainEdge } from '../types';
import { EdgeRenderingServiceAdapter } from './EdgeRenderingServiceAdapter';
import { HydrationRescanScheduler } from './HydrationRescanScheduler';
import { classifyGhostMutationScope } from './reconciliation-intent';

// =============================================================================
// Debug Configuration
// =============================================================================
// =============================================================================
// Types
// =============================================================================

type IncomingAutomationReference = Omit<
  IncomingAutomationInfo,
  'localDatabaseId'
>;

type TrashedLocalAutomationReference = Omit<
  TrashedLocalAutomationInfo,
  'localDatabaseId'
>;

type GfxLayerWithCanvasElements = GfxController['layer'] & {
  canvasElements?: Iterable<GfxModel>;
};

type GfxModelWithModel = GfxModel & {
  model?: unknown;
};

interface OrchestratorDependencies {
  gfx: GfxController;
  store: Store;
  rootId: string;
  container: HTMLElement;
  /** Current document ID (for filtering incoming automations) */
  currentDocId: string;
  /** Resolves after the current Y.Doc completed its authoritative load. */
  waitForInitialSync: () => Promise<void>;
  getWorkspaceDatabases: () => WorkspaceDatabaseInfo[];
  /** Subscribe to workspace database changes for rescan */
  onWorkspaceDatabasesChange?: (callback: () => void) => () => void;
  /**
   * Find automations from other docs that reference a given database.
   * Used for incoming automation ghost creation.
   */
  findIncomingAutomations?: (
    targetDatabaseId: string
  ) => IncomingAutomationReference[];
  /**
   * Find TRASHED automations in the SAME doc that reference a given database.
   * Used for ghost nodes showing deleted automations that had connections.
   */
  findTrashedLocalAutomations?: (
    targetDatabaseId: string
  ) => TrashedLocalAutomationReference[];
  /**
   * Callback to show confirm dialog (for edge deletion).
   * If not provided, falls back to browser confirm.
   */
  onConfirm?: (options: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
  }) => Promise<boolean>;
}

export function createEdgePointerRenderGate({
  isViewportPanning,
  scheduleRender,
}: {
  isViewportPanning: () => boolean;
  scheduleRender: () => void;
}) {
  let pointerActive = false;
  let viewportGesture = false;

  return {
    pointerDown() {
      pointerActive = true;
      viewportGesture = false;
    },
    pointerMove() {
      if (!pointerActive) return;
      if (isViewportPanning()) {
        viewportGesture = true;
        return;
      }
      scheduleRender();
    },
    pointerUp() {
      if (!pointerActive) return;
      pointerActive = false;
      if (!viewportGesture) scheduleRender();
      viewportGesture = false;
    },
  };
}

// =============================================================================
// Implementation
// =============================================================================

/**
 * Orchestrates the reactive edge system.
 *
 * Data flow:
 * ```
 * CanvasReadyGate.ready$
 *       │
 *       ▼
 * BlockRegistry.{databases$, automations$, ghosts$}
 *       │
 *       ├──► GhostDerivation → GhostSync
 *       │
 *       └──► EdgeDerivation → EdgeSync → EdgeRendering
 * ```
 */
export class EdgeOrchestrator implements IEdgeOrchestrator {
  private readyGate: CanvasReadyGate | null = null;
  private blockRegistry: BlockRegistry | null = null;
  private ghostDerivation: GhostDerivationService | null = null;
  private ghostSync: GhostSyncService | null = null;
  private edgeDerivation: EdgeDerivationService | null = null;
  private edgeSync: EdgeSyncService | null = null;
  private renderingService: EdgeRenderingServiceAdapter | null = null;
  private highlightService: EdgeHighlightService | null = null;
  private selectionService: EdgeSelectionService | null = null;

  private pipelineSubscription: Subscription | null = null;
  private renderSubscriptions: Array<{ unsubscribe: () => void }> = [];
  private workspaceDbUnsubscribe: (() => void) | null = null;
  private blockUpdatedUnsubscribe: (() => void) | null = null;
  private hydrationRescanScheduler: HydrationRescanScheduler | null = null;
  private rafId: number | null = null;
  private rescanTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly pendingGhostMutationScopes = new Map<
    string,
    GhostMutationScope
  >();
  private active = false;

  private readonly rescan$ = new Subject<GhostSyncIntent>();

  constructor(private readonly deps: OrchestratorDependencies) {}

  /**
   * Initializes the orchestrator.
   */
  initialize(): void {
    if (this.active) return;

    const { gfx, store, rootId, container } = this.deps;

    // Create CanvasReadyGate
    this.readyGate = new CanvasReadyGate(gfx, store, rootId);
    // Subscribe before awaiting sync so genuine local edits made during load
    // are remembered and reconciled after the authoritative snapshot arrives.
    this.setupBlockUpdatedSubscription(store);

    // Both gates are state-based. No elapsed time grants write permission.
    this.pipelineSubscription = from(this.deps.waitForInitialSync())
      .pipe(
        switchMap(() => this.readyGate!.ready$),
        tap(() => this.setupServices(gfx, store, rootId, container)),
        switchMap(() => this.setupReactivePipeline())
      )
      .subscribe({
        error: err => console.error('[EdgeOrchestrator] Pipeline error:', err),
      });

    // Subscribe to workspace database changes (triggers rescan for ghost metadata updates)
    if (this.deps.onWorkspaceDatabasesChange) {
      this.workspaceDbUnsubscribe = this.deps.onWorkspaceDatabasesChange(() => {
        // Discovery/permission metadata is passive. It may change rendering and
        // diagnostics, but cannot persist into the document by itself.
        this.rescan();
      });
    }

    this.active = true;
  }

  /**
   * Sets up all services.
   */
  private setupServices(
    gfx: GfxController,
    store: Store,
    rootId: string,
    container: HTMLElement
  ): void {
    // Create BlockRegistry
    this.blockRegistry = new BlockRegistry(gfx);

    // Create Derivation Services (pure functions)
    this.ghostDerivation = new GhostDerivationService();
    this.edgeDerivation = new EdgeDerivationService();

    // Create Rendering Service
    this.renderingService = new EdgeRenderingServiceAdapter(gfx);
    this.renderingService.mount(container);

    // Create Highlight Service for selection-based edge highlighting
    // When a database/automation/ghost is selected, its edges go to 100% opacity
    // NOTE: Hover disabled - unreliable (triggers when mouse not over node)
    this.highlightService = new EdgeHighlightService(
      this.renderingService.getInternalService(),
      gfx,
      {
        enableHover: false,
        enableSelection: true,
      }
    );
    this.highlightService.mount(container);

    // Create Selection Service for edge click-to-select and delete
    this.selectionService = new EdgeSelectionService(
      this.renderingService.getInternalService(),
      gfx,
      async edge => this.deleteEdge(edge),
      async edgeType => this.confirmDeleteEdge(edgeType)
    );
    this.selectionService.mount(container);

    // Create Sync Services
    this.ghostSync = new GhostSyncService(store, rootId, (elementId: string) =>
      this.getBound(gfx, elementId)
    );
    this.edgeSync = new EdgeSyncService(this.renderingService);

    // Register Edge Preview Bridge for BlockSuite tools (AutomationConnectorTool)
    this.registerEdgePreviewBridge(gfx);

    // Subscribe to render triggers (viewport, element updates, layer updates, pointer events)
    this.setupRenderSubscriptions(gfx, container);

    this.hydrationRescanScheduler = new HydrationRescanScheduler(() => {
      this.blockRegistry?.forceRescan();
      this.rescan();
      this.renderingService?.render();
    });
    this.hydrationRescanScheduler.start();

    // Initial viewport
    const viewport = gfx.viewport;
    if (viewport) {
      this.renderingService.updateViewport({
        centerX: viewport.centerX,
        centerY: viewport.centerY,
        zoom: viewport.zoom,
        viewportX: viewport.viewportX,
        viewportY: viewport.viewportY,
        width: viewport.width,
        height: viewport.height,
      });
    }
  }

  /**
   * Sets up subscriptions that trigger re-render.
   * Similar to EdgeIntegrationService.subscribeToBlockUpdates
   */
  private setupRenderSubscriptions(
    gfx: GfxController,
    container: HTMLElement
  ): void {
    // RAF-based render to batch updates
    const scheduleRender = () => {
      if (this.rafId === null) {
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          measureGfxPerformance(gfx.std, 'edge-full-render', () =>
            this.renderingService?.render()
          );
        });
      }
    };

    // 1. Subscribe to surface element updates (for canvas elements)
    const surface = gfx.surface;
    if (surface && 'elementUpdated' in surface) {
      const elementUpdated = (
        surface as {
          elementUpdated: {
            subscribe: (
              cb: (data: { id: string; props: Record<string, unknown> }) => void
            ) => { unsubscribe: () => void };
          };
        }
      ).elementUpdated;

      const elementSub = elementUpdated.subscribe(({ props }) => {
        // Re-render when position or rotation changes
        if (props['xywh'] || props['rotate']) {
          scheduleRender();
        }
      });
      this.renderSubscriptions.push(elementSub);
    }

    // 2. Subscribe to layer updates (for block elements like databases)
    const layer = gfx.layer;
    if (layer && 'slots' in layer) {
      const layerSlots = (
        layer as {
          slots?: {
            updated?: {
              subscribe: (cb: () => void) => { unsubscribe: () => void };
            };
          };
        }
      ).slots;
      if (layerSlots?.updated) {
        const layerSub = layerSlots.updated.subscribe(() => {
          scheduleRender();
        });
        this.renderSubscriptions.push(layerSub);
      }
    }

    // 3. Subscribe to viewport updates (for zoom/pan)
    const viewport = gfx.viewport;
    if (viewport) {
      const viewportSub = viewport.viewportUpdated.subscribe(() => {
        scheduleGfxViewportFrame(gfx.std, 'dom', this, () => {
          measureGfxPerformance(
            gfx.std,
            'edge-viewport-transform',
            () => {
              this.renderingService?.updateViewport({
                centerX: viewport.centerX,
                centerY: viewport.centerY,
                zoom: viewport.zoom,
                viewportX: viewport.viewportX,
                viewportY: viewport.viewportY,
                width: viewport.width,
                height: viewport.height,
              });
            },
            undefined,
            () => {
              const expectedValues = [
                viewport.zoom,
                0,
                0,
                viewport.zoom,
                -viewport.viewportX * viewport.zoom,
                -viewport.viewportY * viewport.zoom,
              ];
              const expectedCssTransform = `matrix(${expectedValues.join(', ')})`;
              const edgeGroup =
                container.querySelector<SVGGElement>('#streamlain-edges');
              const cssTransform = edgeGroup?.style.transform ?? null;
              const actualValues = cssTransform
                ?.slice(
                  cssTransform.indexOf('(') + 1,
                  cssTransform.lastIndexOf(')')
                )
                .split(',')
                .map(Number);
              const maxTransformDelta =
                actualValues?.length === expectedValues.length
                  ? Math.max(
                      ...actualValues.map((value, index) =>
                        Math.abs(value - expectedValues[index])
                      )
                    )
                  : null;
              return {
                cssTransform,
                expectedCssTransform,
                maxTransformDelta,
                transformMatchesExpected:
                  maxTransformDelta !== null && maxTransformDelta < 0.001,
                viewportState: viewport.serializeRecord(),
                viewportX: viewport.viewportX,
                viewportY: viewport.viewportY,
                zoom: viewport.zoom,
              };
            }
          );
        });
      });
      this.renderSubscriptions.push(viewportSub);
    }

    // 4. Pointer events for smooth drag updates (like EdgeIntegrationService)
    const pointerRenderGate = createEdgePointerRenderGate({
      isViewportPanning: () => viewport.panning$.value,
      scheduleRender,
    });

    const onPointerDown = () => pointerRenderGate.pointerDown();
    const onPointerUp = () => pointerRenderGate.pointerUp();
    const onPointerMove = () => pointerRenderGate.pointerMove();

    // The GFX host owns canvas interactions. Widgets such as the shared block
    // drag handle are siblings of `.edgeless-container`, so listening on the
    // render container misses their pointer stream and leaves edges following
    // only the slower model-update fallback.
    const pointerEventTarget = gfx.std.host;
    const pointerReleaseTarget = pointerEventTarget.ownerDocument.defaultView;
    pointerEventTarget.addEventListener('pointerdown', onPointerDown, {
      capture: true,
    });
    pointerEventTarget.addEventListener('pointerup', onPointerUp, {
      capture: true,
    });
    pointerEventTarget.addEventListener('pointercancel', onPointerUp, {
      capture: true,
    });
    pointerEventTarget.addEventListener('pointermove', onPointerMove, {
      capture: true,
    });
    pointerEventTarget.addEventListener('lostpointercapture', onPointerUp, {
      capture: true,
    });
    pointerReleaseTarget?.addEventListener('pointerup', onPointerUp, {
      capture: true,
    });
    pointerReleaseTarget?.addEventListener('pointercancel', onPointerUp, {
      capture: true,
    });
    pointerReleaseTarget?.addEventListener('blur', onPointerUp);

    this.renderSubscriptions.push({
      unsubscribe: () => {
        pointerEventTarget.removeEventListener('pointerdown', onPointerDown, {
          capture: true,
        });
        pointerEventTarget.removeEventListener('pointerup', onPointerUp, {
          capture: true,
        });
        pointerEventTarget.removeEventListener('pointercancel', onPointerUp, {
          capture: true,
        });
        pointerEventTarget.removeEventListener('pointermove', onPointerMove, {
          capture: true,
        });
        pointerEventTarget.removeEventListener(
          'lostpointercapture',
          onPointerUp,
          { capture: true }
        );
        pointerReleaseTarget?.removeEventListener('pointerup', onPointerUp, {
          capture: true,
        });
        pointerReleaseTarget?.removeEventListener(
          'pointercancel',
          onPointerUp,
          { capture: true }
        );
        pointerReleaseTarget?.removeEventListener('blur', onPointerUp);
      },
    });
  }

  /**
   * Sets up subscription to store.slots.blockUpdated.
   * CRITICAL: This fires when block properties change (like adding a relation column).
   * Layer updates DON'T fire for property changes - only for add/remove/move!
   */
  private setupBlockUpdatedSubscription(store: Store): void {
    const slots = store.slots as {
      blockUpdated?: {
        subscribe: (
          cb: (payload: {
            id: string;
            flavour: string;
            isLocal: boolean;
          }) => void
        ) => { unsubscribe: () => void };
      };
    };

    if (!slots.blockUpdated) {
      console.warn('[EdgeOrchestrator] store.slots.blockUpdated not available');
      return;
    }

    // Debounced rescan - same pattern as EdgeIntegrationService
    const debouncedRescan = () => {
      if (this.rescanTimeout) clearTimeout(this.rescanTimeout);
      this.rescanTimeout = setTimeout(() => {
        // Before setup, retain user intent for the first authoritative
        // pipeline snapshot instead of emitting into an unsubscribed Subject.
        if (!this.blockRegistry) return;
        // Force BlockRegistry to rescan the layer
        this.blockRegistry?.forceRescan();
        // Then trigger the pipeline
        this.requestReconciliation(this.consumePendingGhostMutationScopes());
      }, 16); // 16ms debounce like EdgeIntegrationService
    };

    const blockSub = slots.blockUpdated.subscribe(payload => {
      const scope = classifyGhostMutationScope(payload);
      if (scope) {
        this.pendingGhostMutationScopes.set(
          `${scope.sourceBlockId}\u0000${scope.ghostType}`,
          scope
        );
      }
      // Ghost writes are reconciler output. Registry/layer emissions already
      // render them; scheduling another persistent pass would create a loop.
      if (payload.flavour !== 'affine:ghost') debouncedRescan();
    });

    this.blockUpdatedUnsubscribe = () => {
      if (this.rescanTimeout) clearTimeout(this.rescanTimeout);
      blockSub.unsubscribe();
    };
  }

  /**
   * Gets bound for an element.
   */
  private getBound(gfx: GfxController, elementId: string): Bound | null {
    const indexed = gfx.getElementById(elementId) as GfxModel | null;
    const indexedBound = this.extractBound(indexed);
    if (indexedBound) return indexedBound;

    const layer = gfx.layer as GfxLayerWithCanvasElements;
    for (const element of layer.blocks) {
      if (element.id !== elementId) continue;
      const bound = this.extractBound(element);
      if (bound) return bound;
    }

    if (layer.canvasElements) {
      for (const element of layer.canvasElements) {
        if (element.id !== elementId) continue;
        const bound = this.extractBound(element);
        if (bound) return bound;
      }
    }

    return null;
  }

  private extractBound(element: GfxModel | null): Bound | null {
    if (element && 'elementBound' in element) {
      return element.elementBound as Bound;
    }

    const model = (element as GfxModelWithModel | null)?.model;
    if (model && typeof model === 'object' && 'elementBound' in model) {
      return model.elementBound as Bound;
    }

    return null;
  }

  /**
   * Sets up the reactive pipeline.
   */
  private setupReactivePipeline(): Observable<void> {
    if (
      !this.blockRegistry ||
      !this.ghostDerivation ||
      !this.ghostSync ||
      !this.edgeDerivation ||
      !this.edgeSync ||
      !this.renderingService
    ) {
      throw new Error('[EdgeOrchestrator] Services not initialized');
    }

    const blockRegistry = this.blockRegistry;
    const ghostDerivation = this.ghostDerivation;
    const ghostSync = this.ghostSync;
    const edgeDerivation = this.edgeDerivation;
    const edgeSync = this.edgeSync;
    const renderingService = this.renderingService;

    const snapshot = (intent: GhostSyncIntent) =>
      [
        blockRegistry.getDatabases(),
        blockRegistry.getAutomations(),
        blockRegistry.getElsters(),
        blockRegistry.getFileSources(),
        blockRegistry.getGhosts(),
        intent,
      ] as const;

    // Combined pipeline - reactive emissions observe only unless a relevant
    // local source change explicitly authorized persistent reconciliation.
    return merge(
      combineLatest([
        blockRegistry.databases$,
        blockRegistry.automations$,
        blockRegistry.elsters$,
        blockRegistry.fileSources$,
        blockRegistry.ghosts$,
      ]).pipe(
        map(([databases, automations, elsters, fileSources, ghosts]) => {
          const intent = this.consumePendingGhostMutationScopes();
          return [
            databases,
            automations,
            elsters,
            fileSources,
            ghosts,
            intent,
          ] as const;
        })
      ),
      this.rescan$.pipe(map(intent => snapshot(intent)))
    ).pipe(
      tap(([databases, automations, elsters, fileSources, ghosts, intent]) => {
        // Get workspace databases for ghost metadata
        const workspaceDatabases = this.deps.getWorkspaceDatabases();

        // Collect incoming automations for all local databases
        // These are automations from OTHER docs that reference our databases
        const incomingAutomations: IncomingAutomationInfo[] = [];
        if (this.deps.findIncomingAutomations) {
          for (const db of databases) {
            const incoming = this.deps.findIncomingAutomations(db.blockId);
            incomingAutomations.push(
              ...incoming.map(i => ({ ...i, localDatabaseId: db.blockId }))
            );
          }
        }

        // Collect TRASHED automations in the SAME doc that reference our databases
        // These create ghosts showing "Automation X is in Trash"
        const trashedLocalAutomations: TrashedLocalAutomationInfo[] = [];
        if (this.deps.findTrashedLocalAutomations) {
          for (const db of databases) {
            const trashed = this.deps.findTrashedLocalAutomations(db.blockId);
            trashedLocalAutomations.push(
              ...trashed.map(t => ({ ...t, localDatabaseId: db.blockId }))
            );
          }
        }

        // STEP 1: Derive and sync ghosts (including incoming + trashed automation ghosts)
        const requiredGhosts = ghostDerivation.deriveRequiredGhosts(
          databases,
          automations,
          workspaceDatabases,
          incomingAutomations,
          trashedLocalAutomations,
          this.deps.currentDocId
        );

        const ghostSyncResult = ghostSync.sync(requiredGhosts, intent);
        if (
          ghostSyncResult.created.length > 0 ||
          ghostSyncResult.deleted.length > 0
        ) {
          requestAnimationFrame(() => {
            this.blockRegistry?.forceRescan();
            this.rescan();
          });
        }

        // NOTE: No manual rescan needed - blockRegistry.ghosts$ will trigger
        // the pipeline automatically when the new ghost appears in the layer

        // STEP 2: Derive and sync edges (with updated ghosts from BlockRegistry)
        // Note: If ghosts were just created, they might not be in BlockRegistry yet
        // The pipeline will re-trigger via ghosts$ when BlockRegistry picks them up
        // E018-S014: Pass incomingAutomations + trashedLocalAutomations for multi-DB ghost edges
        const requiredEdges = edgeDerivation.deriveRequiredEdges(
          databases,
          automations,
          ghosts,
          incomingAutomations,
          trashedLocalAutomations,
          elsters,
          fileSources
        );
        edgeSync.sync(requiredEdges);

        // STEP 3: Render
        renderingService.render();
      }),
      // Convert to void
      map(() => undefined as void)
    );
  }

  /**
   * Whether the orchestrator is active.
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * Gets the block registry.
   */
  getBlockRegistry(): IBlockRegistry | null {
    return this.blockRegistry;
  }

  /**
   * Gets the rendering service.
   */
  getRenderingService(): IEdgeRenderingService | null {
    return this.renderingService;
  }

  /**
   * Forces a rescan.
   */
  rescan(): void {
    this.requestReconciliation({ kind: 'observe' });
  }

  private consumePendingGhostMutationScopes(): GhostSyncIntent {
    if (this.pendingGhostMutationScopes.size === 0) {
      return { kind: 'observe' };
    }

    const scopes = Array.from(this.pendingGhostMutationScopes.values());
    this.pendingGhostMutationScopes.clear();
    return { kind: 'user-change', scopes };
  }

  private requestReconciliation(intent: GhostSyncIntent): void {
    this.rescan$.next(intent);
  }

  // ===========================================================================
  // Edge Preview Bridge (for BlockSuite tools)
  // ===========================================================================

  /**
   * Registers the edge preview bridge on the window object.
   * This allows BlockSuite tools (like AutomationConnectorTool) to render
   * preview edges using the same rendering system as permanent edges.
   *
   * Bridge Pattern: BlockSuite Layer → Frontend Layer communication
   *
   * @see sl-cmx for AutomationConnectorTool that uses this bridge
   */
  private registerEdgePreviewBridge(gfx: GfxController): void {
    const renderingService = this.renderingService;
    if (!renderingService) return;

    const internalService = renderingService.getInternalService();
    const connectorHighlightClass =
      'streamlain-automation-connector-target-highlight';
    const connectorHighlightStyleId =
      'streamlain-automation-connector-target-highlight-style';
    const highlightedViews = new Set<HTMLElement>();
    if (!document.getElementById(connectorHighlightStyleId)) {
      const style = document.createElement('style');
      style.id = connectorHighlightStyleId;
      style.textContent = `
        .${connectorHighlightClass} {
          outline: 3px dashed var(--streamlain-v2-button-primary, var(--affine-primary-color, #1e96eb)) !important;
          outline-offset: 3px;
        }
      `;
      document.head.append(style);
    }
    const setConnectorHighlights = (elementIds: string[]): void => {
      for (const view of highlightedViews) {
        view.classList.remove(connectorHighlightClass);
      }
      highlightedViews.clear();
      for (const elementId of elementIds) {
        const view = gfx.std?.view.getBlock(elementId) as HTMLElement | null;
        if (!view) continue;
        view.classList.add(connectorHighlightClass);
        highlightedViews.add(view);
      }
    };

    // Define the bridge interface
    const bridge = {
      /**
       * Starts a preview edge from a source element to cursor position.
       * @param sourceId - Source element ID (Database or Automation block)
       * @param cursorX - Cursor X in VIEW coordinates (screen pixels)
       * @param cursorY - Cursor Y in VIEW coordinates (screen pixels)
       * @param type - Edge type ('automation' or 'relation')
       */
      addPreviewEdge: (
        sourceId: string,
        cursorX: number,
        cursorY: number,
        type: 'automation' | 'relation' = 'automation',
        automationDirection?: 'input' | 'output'
      ): void => {
        // Convert view coords to model coords
        const [modelX, modelY] = gfx.viewport.toModelCoord(cursorX, cursorY);
        internalService.addPreviewEdge(
          sourceId,
          { x: modelX, y: modelY },
          type,
          automationDirection
        );
      },

      /**
       * Updates the preview edge target position.
       * @param cursorX - Cursor X in VIEW coordinates
       * @param cursorY - Cursor Y in VIEW coordinates
       */
      updatePreviewEdge: (cursorX: number, cursorY: number): void => {
        const [modelX, modelY] = gfx.viewport.toModelCoord(cursorX, cursorY);
        internalService.updatePreviewEdge({ x: modelX, y: modelY });
      },

      snapPreviewEdge: (targetId: string): void => {
        internalService.snapPreviewEdge(targetId);
      },

      commitPreviewEdge: (): void => {
        internalService.commitPreviewEdge();
      },

      setConnectorHighlights,

      /**
       * Sets whether the current target is valid (affects visual styling).
       * @param isValid - True for valid target, false for invalid
       */
      setPreviewValidity: (isValid: boolean): void => {
        internalService.setPreviewValidity(isValid);
      },

      /**
       * Removes the preview edge.
       */
      removePreviewEdge: (): void => {
        internalService.removePreviewEdge();
        setConnectorHighlights([]);
      },

      /**
       * Checks if a preview edge is currently active.
       */
      hasPreviewEdge: (): boolean => {
        return internalService.hasPreviewEdge();
      },
    };

    // Register on window
    (
      window as Window & { __edgePreviewBridge?: typeof bridge }
    ).__edgePreviewBridge = bridge;
  }

  /**
   * Unregisters the edge preview bridge from the window object.
   */
  private unregisterEdgePreviewBridge(): void {
    delete (window as Window & { __edgePreviewBridge?: unknown })
      .__edgePreviewBridge;
  }

  /**
   * Disposes the orchestrator.
   */
  dispose(): void {
    // Unregister edge preview bridge
    this.unregisterEdgePreviewBridge();
    cancelGfxViewportFrame(this.deps.gfx.std, 'dom', this);
    // Cancel any pending RAF
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Unsubscribe render subscriptions
    for (const sub of this.renderSubscriptions) {
      sub.unsubscribe();
    }
    this.renderSubscriptions = [];

    // Unsubscribe workspace database changes
    this.workspaceDbUnsubscribe?.();
    this.workspaceDbUnsubscribe = null;

    // Unsubscribe block updated subscription
    this.blockUpdatedUnsubscribe?.();
    this.blockUpdatedUnsubscribe = null;

    this.hydrationRescanScheduler?.stop();
    this.hydrationRescanScheduler = null;

    this.pipelineSubscription?.unsubscribe();
    this.pipelineSubscription = null;

    this.edgeSync?.dispose();
    this.edgeSync = null;

    this.ghostSync?.dispose();
    this.ghostSync = null;

    // Unmount selection and highlight services before rendering service
    this.selectionService?.unmount();
    this.selectionService = null;

    this.highlightService?.unmount();
    this.highlightService = null;

    this.renderingService?.unmount();
    this.renderingService = null;

    this.blockRegistry?.dispose();
    this.blockRegistry = null;

    this.readyGate?.dispose();
    this.readyGate = null;

    this.ghostDerivation = null;
    this.edgeDerivation = null;

    this.active = false;
  }

  // ===========================================================================
  // Edge Deletion (sl-rud)
  // ===========================================================================

  /**
   * Deletes an edge by removing the connection from the automation block.
   * Returns true if deletion was successful.
   */
  private async deleteEdge(edge: StreamlainEdge): Promise<boolean> {
    const store = this.deps.store;
    if (!store) return false;

    // Use metadata if available (robust: uses blockId, not elementId)
    if (edge.metadata?.kind === 'automation') {
      return this.deleteAutomationEdgeViaMetadata(store, edge.metadata);
    }

    // Relation edges: not deletable via keyboard (no model mutation needed)
    if (edge.type === 'relation') {
      console.warn(
        '[EdgeOrchestrator] Relation edges cannot be deleted directly',
        edge.id
      );
      return false;
    }

    // Fallback for edges without metadata (legacy path)
    return this.deleteAutomationEdgeLegacy(store, edge);
  }

  /**
   * Deletes an automation edge using metadata (automationId, databaseId, direction).
   * Robust: uses block IDs from derivation, not element IDs.
   */
  private deleteAutomationEdgeViaMetadata(
    store: Store,
    meta: AutomationEdgeMetadata
  ): boolean {
    const autoBlock = store.getBlock(meta.automationId);
    if (!autoBlock) {
      console.warn(
        '[EdgeOrchestrator] Automation block not found',
        meta.automationId
      );
      return false;
    }

    const model = autoBlock.model as {
      flavour: string;
      props?: {
        executionKind?: 'workflow' | 'cleanup';
        sourceTableId?: string | null;
        sourceTableName?: string | null;
        sourceDocId?: string | null;
        outputTables?: Array<{ tableId: string }>;
        inputTableId?: string | null;
        inputTableName?: string | null;
        inputDocId?: string | null;
      };
    };

    if (model.flavour === 'affine:automation') {
      if (model.props?.executionKind === 'cleanup') {
        if (model.props.sourceTableId !== meta.databaseId) return false;
        writeCleanupBinding(
          autoBlock.model as AutomationBlockModel,
          null,
          meta.direction
        );
        return true;
      }
      if (meta.direction === 'input') {
        store.transact(() => {
          if (model.props) {
            model.props.sourceTableId = null;
            model.props.sourceTableName = null;
            model.props.sourceDocId = null;
          }
        });
        return true;
      }
      // output
      const hasOutput = model.props?.outputTables?.some(
        o => o.tableId === meta.databaseId
      );
      if (!hasOutput) {
        console.warn(
          '[EdgeOrchestrator] Output table not found in automation',
          meta.databaseId
        );
        return false;
      }
      store.transact(() => {
        if (model.props?.outputTables) {
          model.props.outputTables = model.props.outputTables.filter(
            o => o.tableId !== meta.databaseId
          );
        }
      });
      return true;
    }

    if (model.flavour === 'affine:elster') {
      if (meta.direction === 'input') {
        store.transact(() => {
          if (model.props) {
            model.props.inputTableId = null;
            model.props.inputTableName = null;
            model.props.inputDocId = null;
          }
        });
        return true;
      }
      console.warn('[EdgeOrchestrator] ELSTER blocks do not have output edges');
      return false;
    }

    console.warn('[EdgeOrchestrator] Unknown processor flavour', model.flavour);
    return false;
  }

  /**
   * Legacy fallback: deletes automation edge using elementId-based heuristics.
   * Fragile for Note-wrapped databases where elementId !== blockId.
   */
  private deleteAutomationEdgeLegacy(
    store: Store,
    edge: StreamlainEdge
  ): boolean {
    const sourceBlock = store.getBlock(edge.sourceId);
    const targetBlock = store.getBlock(edge.targetId);

    if (!sourceBlock || !targetBlock) {
      console.warn(
        '[EdgeOrchestrator] Could not find blocks for edge',
        edge.id
      );
      return false;
    }

    const sourceModel = sourceBlock.model as {
      flavour: string;
      props?: {
        executionKind?: 'workflow' | 'cleanup';
        sourceTableId?: string | null;
        sourceTableName?: string | null;
        sourceDocId?: string | null;
        outputTables?: Array<{ tableId: string }>;
        inputTableId?: string | null;
        inputTableName?: string | null;
        inputDocId?: string | null;
      };
    };
    const targetModel = targetBlock.model as typeof sourceModel;

    let processorModel: typeof sourceModel | null = null;
    let processorFlavour: string | null = null;
    let databaseId: string | null = null;

    if (
      sourceModel.flavour === 'affine:automation' ||
      sourceModel.flavour === 'affine:elster'
    ) {
      processorModel = sourceModel;
      processorFlavour = sourceModel.flavour;
      databaseId = edge.targetId;
    } else if (
      targetModel.flavour === 'affine:automation' ||
      targetModel.flavour === 'affine:elster'
    ) {
      processorModel = targetModel;
      processorFlavour = targetModel.flavour;
      databaseId = edge.sourceId;
    }

    if (!processorModel || !processorFlavour || !databaseId) {
      console.warn(
        '[EdgeOrchestrator] Edge is not a processor edge (legacy)',
        edge.id
      );
      return false;
    }

    if (processorFlavour === 'affine:automation') {
      if (processorModel.props?.executionKind === 'cleanup') {
        if (processorModel.props.sourceTableId !== databaseId) return false;
        const block = store.getBlock(
          sourceModel.flavour === 'affine:automation'
            ? edge.sourceId
            : edge.targetId
        );
        if (!block) return false;
        writeCleanupBinding(block.model as AutomationBlockModel, null, 'input');
        return true;
      }
      const isInput = processorModel.props?.sourceTableId === databaseId;
      const isOutput = processorModel.props?.outputTables?.some(
        o => o.tableId === databaseId
      );

      if (!isInput && !isOutput) {
        console.warn(
          '[EdgeOrchestrator] Edge not found in automation model (legacy)',
          edge.id
        );
        return false;
      }

      store.transact(() => {
        if (isInput && processorModel?.props) {
          processorModel.props.sourceTableId = null;
          processorModel.props.sourceTableName = null;
          processorModel.props.sourceDocId = null;
        } else if (isOutput && processorModel?.props?.outputTables) {
          processorModel.props.outputTables =
            processorModel.props.outputTables.filter(
              o => o.tableId !== databaseId
            );
        }
      });
    } else {
      // affine:elster
      const isInput = processorModel.props?.inputTableId === databaseId;
      if (!isInput) {
        console.warn(
          '[EdgeOrchestrator] Edge not found in ELSTER model (legacy)',
          edge.id
        );
        return false;
      }
      store.transact(() => {
        if (isInput && processorModel?.props) {
          processorModel.props.inputTableId = null;
          processorModel.props.inputTableName = null;
          processorModel.props.inputDocId = null;
        }
      });
    }

    return true;
  }

  /**
   * Shows a confirmation dialog for edge deletion with i18n support.
   * sl-optc: Added "Don't show again" checkbox support.
   */
  private async confirmDeleteEdge(
    edgeType: 'automation' | 'relation'
  ): Promise<boolean> {
    // Get translated edge type
    const edgeTypeKey =
      edgeType === 'automation'
        ? 'com.streamlain.streamlain.edge.type.automation'
        : 'com.streamlain.streamlain.edge.type.relation';
    const translatedEdgeType = I18n.t(edgeTypeKey);

    // Get translated strings
    const title = I18n.t('com.streamlain.streamlain.edge.delete.title');
    const message = I18n.t('com.streamlain.streamlain.edge.delete.message', {
      edgeType: translatedEdgeType,
    });
    const confirmText = I18n.t('com.streamlain.streamlain.edge.delete.confirm');
    const cancelText = I18n.t('com.streamlain.streamlain.edge.delete.cancel');

    // sl-optc: Key for "don't show again" preference (per edge type)
    const dontShowAgainKey = `deleteEdge:${edgeType}`;

    // Try NotificationProvider from BlockSuite std
    const notificationService =
      this.deps.gfx.std.getOptional(NotificationProvider);
    if (notificationService) {
      return notificationService.confirm({
        title,
        message,
        confirmText,
        cancelText,
        dontShowAgainKey,
      });
    }

    // Try onConfirm callback
    if (this.deps.onConfirm) {
      return this.deps.onConfirm({ title, message, confirmText, cancelText });
    }

    // Fallback to browser confirm
    return window.confirm(`${title}\n\n${message}`);
  }
}
